import React from "react";
import { Loader2, ShieldCheck, UserCheck } from "lucide-react";

const roleOptions = [
  {
    value: "manager",
    label: "Manager",
    description: "Require a manager account password before a paid transaction can be voided.",
  },
  {
    value: "supervisor",
    label: "Supervisor",
    description: "Require a supervisor account password before a paid transaction can be voided.",
  },
  {
    value: "cashier",
    label: "Cashier",
    description: "Allow cashier accounts to void paid transactions using their own password.",
  },
  {
    value: "all_staff",
    label: "All Staff",
    description: "Allow cashier, supervisor, manager, or staff role passwords to void paid transactions.",
  },
];

export default function OperationsSection({
  preferences,
  appliedPreferences,
  loading,
  saving,
  error,
  success,
  onChange,
  onSubmit,
}) {
  const selectedRole = preferences?.void_authorized_role || "manager";
  const appliedRole = appliedPreferences?.void_authorized_role || selectedRole;

  return (
    <form className="flex flex-col gap-4 px-5 pb-5" onSubmit={onSubmit}>
      <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
            style={{
              backgroundColor: "color-mix(in srgb, var(--color-primary) 12%, white)",
              color: "var(--color-primary)",
            }}
          >
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Void Authorization</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Choose which staff role can approve voiding a paid transaction. Admin owners can always authorize with their password.
            </p>
          </div>
        </div>
      </div>

      <fieldset disabled={loading || saving} className="grid gap-3 disabled:cursor-wait disabled:opacity-70">
        {roleOptions.map((option) => {
          const selected = selectedRole === option.value;
          const applied = appliedRole === option.value;
          return (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-2xl border bg-white/90 p-4 shadow-sm transition ${
                selected ? "border-slate-900 ring-2 ring-slate-100" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="void_authorized_role"
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                className="mt-0 h-4 w-4 self-center accent-[var(--color-primary)]"
              />
              <span className="flex min-w-0 flex-1 gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100"
                  style={{ color: selected ? "var(--color-primary)" : "#64748b" }}
                >
                  <UserCheck className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                    {applied && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        Applied
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">{option.description}</span>
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {error && <div className="text-xs text-red-500">{error}</div>}
      {success && <div className="text-xs text-emerald-600">{success}</div>}

      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-800">
        The Void button can still be opened from any register. The entered password must belong to the selected role or an admin owner.
      </div>

      <button
        type="submit"
        disabled={loading || saving}
        className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-base font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.16)] transition hover:-translate-y-0.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        style={{ background: "var(--color-primary)" }}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
        {saving ? "Saving..." : "Save Operations"}
      </button>
    </form>
  );
}
